"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  EU_CASE_FIELDS as C,
  type EuLawLabels,
  euCaseIsReady,
  euLegislationIsReady,
  formatEuCase,
  formatEuLegislation,
  EU_LEGISLATION_FIELDS as L,
} from "@/lib/tool-kit/legal-sources/eu-law-citation-generator";

type Source = "legislation" | "case";

/** English strings. A translated content file overrides these through `ui`. */
export const EU_LAW_CITATION_GENERATOR_STRINGS = {
  formHeading: "EU source details",
  resultsHeading: "Your citations",
  emptyPromptLegislation:
    "Enter the act's type and number and its Official Journal reference to see your citations.",
  emptyPromptCase:
    "Enter the case number, the case name and the date of the judgment to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  sourceLabel: "What you are citing",
  sourceLegislation: "A regulation, directive or decision",
  sourceCase: "A judgment of the Court of Justice or General Court",
  designationLabel: "Type and number, as printed",
  designationPlaceholder: "Regulation (EU) 2016/679",
  designationHint:
    "Since 2015 every act is numbered (EU) year/number. Older ones: Directive 95/46/EC, Regulation (EC) No 1907/2006.",
  institutionsLabel: "Adopted by (optional)",
  institutionsPlaceholder: "of the European Parliament and of the Council",
  adoptedLabel: "Date adopted",
  adoptedPlaceholder: "YYYY-MM-DD",
  subjectLabel: "Rest of the title",
  subjectPlaceholder:
    "on the protection of natural persons with regard to the processing of personal data and on the free movement of such data, and repealing Directive 95/46/EC (General Data Protection Regulation)",
  subjectHint:
    "Everything after the date in the official title, starting with on… or laying down….",
  ojLabel: "Official Journal reference, as EUR-Lex prints it",
  ojPlaceholder: "OJ L 119, 4.5.2016, p. 1–88",
  ojHint:
    "Acts published from October 2023 have no issue or page: OJ L, 2024/1689, 12.7.2024.",
  articleLabel: "Article you cite (optional, OSCOLA)",
  articlePlaceholder: "17",
  shortTitleLabel: "Short form for later footnotes (optional, OSCOLA)",
  shortTitlePlaceholder: "GDPR",
  urlLabel: "URL",
  urlPlaceholderLegislation: "https://eur-lex.europa.eu/eli/reg/2016/679/oj",
  accessedLabel: "Date you accessed it (Harvard, online sources)",
  accessedPlaceholder: "YYYY-MM-DD",
  caseNumberLabel: "Case number",
  caseNumberPlaceholder: "C-311/18",
  caseNumberHint: "C- for the Court of Justice, T- for the General Court.",
  caseNameLabel: "Case name",
  caseNamePlaceholder:
    "Data Protection Commissioner v Facebook Ireland Limited and Maximillian Schrems",
  ecliLabel: "ECLI",
  ecliPlaceholder: "ECLI:EU:C:2020:559",
  ecliHint: "The European Case Law Identifier, shown on CURIA and EUR-Lex.",
  judgmentDateLabel: "Date of the judgment",
  judgmentDatePlaceholder: "YYYY-MM-DD",
  paragraphLabel: "Paragraph you cite (optional, OSCOLA)",
  paragraphPlaceholder: "94",
  urlPlaceholderCase:
    "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:62018CJ0311",
  oscolaLabel: "OSCOLA (5th edition)",
  apaLabel: "APA 7th edition",
  harvardLabel: "Harvard (Cite Them Right)",
  footnote: "Footnote",
  tableOfLegislation: "Table of legislation",
  tableOfCases: "Table of cases",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  inText: "In-text citation",
  oscolaNote:
    "OSCOLA puts the year in square brackets and closes up the issue and page (L119/1). Later footnotes can use the short form alone.",
  oscolaActByActNote:
    "OSCOLA's printed examples predate act-by-act publication (October 2023); this follows the EU's own reference, OJ L, year/number, with OSCOLA punctuation. Check your institution's guidance.",
  apaNote:
    "APA 7 has no EU template. This follows its legal-reference order — title, source, year — with the Official Journal reference the EU itself prints.",
  harvardNote:
    "Cite Them Right puts the act's type, number and title in single quotation marks and italicizes Official Journal. Online-only sources take Available at and an access date.",
  oscolaCaseNote:
    "OSCOLA's fifth edition cites EU judgments by case number, italic case name and ECLI, with no law report; paragraph pinpoints go in square brackets.",
  apaCaseNote:
    "APA 7 has no EU template. This follows its court-decision pattern: the case name in roman type in the reference list and in italics in text.",
  harvardCaseNote:
    "Cite Them Right puts the case name in single quotation marks and adds Case no. and the ECLI; a judgment read online takes Available at and an access date.",
  ojUnreadable:
    "Couldn't read the Official Journal reference. Type it as EUR-Lex prints it — OJ L 119, 4.5.2016, p. 1–88, or OJ L, 2024/1689, 12.7.2024.",
  exampleLegislation: {
    designation: "Regulation (EU) 2016/679",
    institutions: "of the European Parliament and of the Council",
    adopted: "2016-04-27",
    subject:
      "on the protection of natural persons with regard to the processing of personal data and on the free movement of such data, and repealing Directive 95/46/EC (General Data Protection Regulation)",
    oj: "OJ L 119, 4.5.2016, p. 1–88",
    article: "17",
    shortTitle: "GDPR",
    url: "https://eur-lex.europa.eu/eli/reg/2016/679/oj",
  },
  exampleCase: {
    caseNumber: "C-311/18",
    caseName:
      "Data Protection Commissioner v Facebook Ireland Limited and Maximillian Schrems",
    ecli: "ECLI:EU:C:2020:559",
    date: "2020-07-16",
    url: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:62018CJ0311",
    accessed: "2026-10-05",
  },
} as const;

type Strings = typeof EU_LAW_CITATION_GENERATOR_STRINGS;

function legislationFields(strings: Strings): SourceField[] {
  return [
    {
      id: L.designation,
      kind: "text",
      label: strings.designationLabel,
      placeholder: strings.designationPlaceholder,
      hint: strings.designationHint,
    },
    {
      id: L.institutions,
      kind: "text",
      label: strings.institutionsLabel,
      placeholder: strings.institutionsPlaceholder,
    },
    {
      id: L.adopted,
      kind: "date",
      label: strings.adoptedLabel,
      placeholder: strings.adoptedPlaceholder,
    },
    {
      id: L.oj,
      kind: "text",
      label: strings.ojLabel,
      placeholder: strings.ojPlaceholder,
      hint: strings.ojHint,
    },
    {
      id: L.subject,
      kind: "textarea",
      label: strings.subjectLabel,
      placeholder: strings.subjectPlaceholder,
      hint: strings.subjectHint,
    },
    {
      id: L.article,
      kind: "text",
      label: strings.articleLabel,
      placeholder: strings.articlePlaceholder,
    },
    {
      id: L.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
    },
    {
      id: L.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholderLegislation,
    },
    {
      id: L.accessed,
      kind: "date",
      label: strings.accessedLabel,
      placeholder: strings.accessedPlaceholder,
    },
  ];
}

function caseFields(strings: Strings): SourceField[] {
  return [
    {
      id: C.caseNumber,
      kind: "text",
      label: strings.caseNumberLabel,
      placeholder: strings.caseNumberPlaceholder,
      hint: strings.caseNumberHint,
    },
    {
      id: C.ecli,
      kind: "text",
      label: strings.ecliLabel,
      placeholder: strings.ecliPlaceholder,
      hint: strings.ecliHint,
    },
    {
      id: C.caseName,
      kind: "text",
      label: strings.caseNameLabel,
      placeholder: strings.caseNamePlaceholder,
      wide: true,
    },
    {
      id: C.date,
      kind: "date",
      label: strings.judgmentDateLabel,
      placeholder: strings.judgmentDatePlaceholder,
    },
    {
      id: C.paragraph,
      kind: "text",
      label: strings.paragraphLabel,
      placeholder: strings.paragraphPlaceholder,
    },
    {
      id: C.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholderCase,
    },
    {
      id: C.accessed,
      kind: "date",
      label: strings.accessedLabel,
      placeholder: strings.accessedPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function euLawLabels(strings: Strings): EuLawLabels {
  return {
    oscola: strings.oscolaLabel,
    apa: strings.apaLabel,
    harvard: strings.harvardLabel,
    footnote: strings.footnote,
    tableOfLegislation: strings.tableOfLegislation,
    tableOfCases: strings.tableOfCases,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    inText: strings.inText,
    oscolaNote: strings.oscolaNote,
    oscolaActByActNote: strings.oscolaActByActNote,
    apaNote: strings.apaNote,
    harvardNote: strings.harvardNote,
    oscolaCaseNote: strings.oscolaCaseNote,
    apaCaseNote: strings.apaCaseNote,
    harvardCaseNote: strings.harvardCaseNote,
    ojUnreadable: strings.ojUnreadable,
  };
}

export default function EuLawCitationGeneratorWidget() {
  const strings = useToolUi(EU_LAW_CITATION_GENERATOR_STRINGS);
  const [source, setSource] = useState<Source>("legislation");
  const isLegislation = source === "legislation";
  const fields = useMemo(
    () => (isLegislation ? legislationFields(strings) : caseFields(strings)),
    [isLegislation, strings]
  );
  const labels = useMemo(() => euLawLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) =>
      isLegislation
        ? formatEuLegislation(values, labels)
        : formatEuCase(values, labels),
    [isLegislation, labels]
  );
  const builderStrings = useMemo(
    () => ({
      ...strings,
      emptyPrompt: isLegislation
        ? strings.emptyPromptLegislation
        : strings.emptyPromptCase,
    }),
    [isLegislation, strings]
  );

  return (
    <SourceCitationBuilder
      example={isLegislation ? strings.exampleLegislation : strings.exampleCase}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.sourceLabel}
          name="eu-law-citation-source"
          onChange={setSource}
          options={[
            { value: "legislation", label: strings.sourceLegislation },
            { value: "case", label: strings.sourceCase },
          ]}
          value={source}
        />
      }
      isReady={isLegislation ? euLegislationIsReady : euCaseIsReady}
      strings={builderStrings}
    />
  );
}
